// Chibi character sheet generator — 48×56 logical cells at pixelScale 2
// → 96×112 canvas per frame. Sheet has 20 rows (4 dirs × 5 states) ×
// 8 cols. Three body-type drawers dispatch by faction:
//
//   Virgin  — fat / slouched / baggy clothes / glasses / messy hair / book
//   Chad    — V-shape torso / upright / tank top / mohawk / phone
//   Civilian — neutral rectangle / simple hair / basic clothes
//
// Each body type has its OWN pose curves (see rig.ts POSE_CURVES) so
// movement feel differs: Virgin shuffles heavy, Chad struts, civilian
// walks standard.
//
// All drawing uses the shared canvas helpers so pack swapping remains
// clean.

import type { Sheet, CharacterColors, BodyShape } from '../../sprites';
import { newCanvas, makeTexture, px, rect } from '../canvas_utils';
import { darken, lighten } from '../draw_utils';
import {
  NEUTRAL, VIRGIN, CHAD, classifyFaction, type Faction,
} from './palette';
import {
  CELL_W, CELL_H, BASE, BODY,
  STATE_META, SHEET_COLS, SHEET_ROWS,
  POSE_CURVES,
  type BodyType,
} from './rig';

const PIXEL_SCALE = 2;
const FRAME_W = CELL_W * PIXEL_SCALE; // 96
const FRAME_H = CELL_H * PIXEL_SCALE; // 112

type AnimState = 'idle' | 'walk' | 'attack' | 'hurt' | 'death';

// Direction constants matching src/sprites.ts exports.
const DIR_DOWN = 0;
const DIR_LEFT = 1;
const DIR_RIGHT = 2;
const DIR_UP = 3;

type Tones = {
  outline: string;
  shirtDeep: string;
  shirtBase: string;
  shirtHi: string;
  pants: string;
  pantsHi: string;
  pantsShadow: string;
  skin: string;
  skinHi: string;
  skinShadow: string;
  shoe: string;
  hair: string;
  hairHi: string;
  accent: string;
  accentHi: string;
  rim: string;
};

function buildTones(c: CharacterColors, faction: Faction): Tones {
  const fp = faction === 'virgin' ? VIRGIN : faction === 'chad' ? CHAD : null;
  const shirt = c.shirt;
  const outline = fp ? fp.outline : darken(shirt, 0.6);
  return {
    outline,
    shirtDeep: fp ? fp.deep : darken(shirt, 0.35),
    shirtBase: shirt,
    shirtHi:   fp ? fp.highlight : lighten(shirt, 0.2),
    pants: c.pants,
    pantsHi: lighten(c.pants, 0.12),
    pantsShadow: darken(c.pants, 0.3),
    skin: c.skin,
    skinHi: lighten(c.skin, 0.1),
    skinShadow: darken(c.skin, 0.18),
    shoe: c.shoes,
    hair: NEUTRAL.darkSlate,
    hairHi: NEUTRAL.stone,
    accent: fp ? fp.accent : NEUTRAL.stone,
    accentHi: fp ? fp.accentHi : NEUTRAL.cloud,
    rim: fp ? fp.rim : NEUTRAL.ivory,
  };
}

// ---- Canvas helpers (logical → canvas scaling) ----
function lrect(
  ctx: CanvasRenderingContext2D,
  ox: number, oy: number,
  x: number, y: number, w: number, h: number,
  color: string,
) {
  rect(ctx, ox + x * PIXEL_SCALE, oy + y * PIXEL_SCALE, w * PIXEL_SCALE, h * PIXEL_SCALE, color);
}
function lpx(
  ctx: CanvasRenderingContext2D,
  ox: number, oy: number,
  x: number, y: number,
  color: string,
) {
  rect(ctx, ox + x * PIXEL_SCALE, oy + y * PIXEL_SCALE, PIXEL_SCALE, PIXEL_SCALE, color);
  void px; // keep the import used
}

// ---- Pose offset lookup — returns arrays indexable by frame ----
function getPose(body: BodyType, state: AnimState, frame: number): Record<string, number> {
  const curves = POSE_CURVES[body][state] as Record<string, ReadonlyArray<number>>;
  const out: Record<string, number> = {};
  for (const k of Object.keys(curves)) {
    const arr = curves[k];
    out[k] = arr[frame] ?? 0;
  }
  return out;
}

// ============================================================
// HEAD (shared shape, body-type specific hair/accessory)
// ============================================================

function drawHeadChibi(
  ctx: CanvasRenderingContext2D, ox: number, oy: number,
  t: Tones, dir: number, headDy: number, body: BodyType,
) {
  const cx = BASE.headCx;
  const top = BASE.headTop + headDy;
  const bot = BASE.headBottom + headDy;
  const hw = BASE.headHalfW;

  // ---- Skull silhouette ----
  // Rounded rect: 20 wide, 18 tall
  // Top row & bottom row are 1px narrower for chibi roundness
  lrect(ctx, ox, oy, cx - hw + 1, top,     hw * 2 - 2, 1, t.outline);
  lrect(ctx, ox, oy, cx - hw,     top + 1, 1, bot - top - 2, t.outline);
  lrect(ctx, ox, oy, cx + hw - 1, top + 1, 1, bot - top - 2, t.outline);
  lrect(ctx, ox, oy, cx - hw + 1, bot - 1, hw * 2 - 2, 1, t.outline);

  // Skin fill
  lrect(ctx, ox, oy, cx - hw + 1, top + 1, hw * 2 - 2, bot - top - 2, t.skin);
  // Light side highlight
  const rightSide = dir !== DIR_LEFT;
  const shadeSide = dir === DIR_LEFT;
  if (rightSide) {
    lrect(ctx, ox, oy, cx + hw - 2, top + 2, 1, bot - top - 4, t.skinHi);
  }
  if (shadeSide || dir === DIR_DOWN) {
    lrect(ctx, ox, oy, cx - hw + 1, top + 2, 1, bot - top - 4, t.skinShadow);
  }

  // ---- Body-type hair / head accessories ----
  if (body === 'virgin') {
    // Messy dark bangs covering forehead
    lrect(ctx, ox, oy, cx - hw + 1, top + 1, hw * 2 - 2, 3, t.hair);
    // Jagged edge — pixel zigzag
    lpx(ctx, ox, oy, cx - hw + 2, top + 4, t.hair);
    lpx(ctx, ox, oy, cx - 1, top + 4, t.hair);
    lpx(ctx, ox, oy, cx + 3, top + 4, t.hair);
    lpx(ctx, ox, oy, cx + hw - 2, top + 4, t.hair);
    // Hair tuft sticking up
    lpx(ctx, ox, oy, cx - 1, top - 1, t.hair);
    lpx(ctx, ox, oy, cx - 1, top - 2, t.hair);

    // Glasses — dark rectangle frames over eye line
    if (dir === DIR_DOWN) {
      lrect(ctx, ox, oy, cx - 5, top + 7, 3, 2, NEUTRAL.ink);
      lrect(ctx, ox, oy, cx + 2, top + 7, 3, 2, NEUTRAL.ink);
      lpx(ctx, ox, oy, cx - 2, top + 7, NEUTRAL.ink);    // bridge
      lpx(ctx, ox, oy, cx + 1, top + 7, NEUTRAL.ink);
      // Lens glint
      lpx(ctx, ox, oy, cx - 4, top + 7, NEUTRAL.cloud);
      lpx(ctx, ox, oy, cx + 3, top + 7, NEUTRAL.cloud);
    } else if (dir === DIR_LEFT) {
      lrect(ctx, ox, oy, cx - 5, top + 7, 3, 2, NEUTRAL.ink);
      lpx(ctx, ox, oy, cx - 4, top + 7, NEUTRAL.cloud);
    } else if (dir === DIR_RIGHT) {
      lrect(ctx, ox, oy, cx + 2, top + 7, 3, 2, NEUTRAL.ink);
      lpx(ctx, ox, oy, cx + 3, top + 7, NEUTRAL.cloud);
    }

    // Mouth — slight frown (virgin vibe)
    if (dir === DIR_DOWN) {
      lrect(ctx, ox, oy, cx - 2, top + 12, 4, 1, t.skinShadow);
    }

  } else if (body === 'chad') {
    // Mohawk — bright spike on top, shaved sides
    lrect(ctx, ox, oy, cx - 1, top - 3, 2, 3, t.accent);  // tall spike
    lrect(ctx, ox, oy, cx - 2, top, 4, 2, t.accent);
    // Darker shaved base
    lrect(ctx, ox, oy, cx - hw + 1, top + 1, hw * 2 - 2, 1, darken(t.skin, 0.2));

    // Eyes — narrowed confident
    if (dir === DIR_DOWN) {
      lrect(ctx, ox, oy, cx - 4, top + 7, 2, 1, NEUTRAL.ink);
      lrect(ctx, ox, oy, cx + 2, top + 7, 2, 1, NEUTRAL.ink);
      // Eye shine
      lpx(ctx, ox, oy, cx - 3, top + 7, NEUTRAL.ivory);
      lpx(ctx, ox, oy, cx + 3, top + 7, NEUTRAL.ivory);
      // Jawline shadow
      lpx(ctx, ox, oy, cx - 4, top + 13, t.skinShadow);
      lpx(ctx, ox, oy, cx + 3, top + 13, t.skinShadow);
      // Chiseled chin
      lrect(ctx, ox, oy, cx - 1, top + 14, 2, 1, t.skinShadow);
    } else if (dir === DIR_LEFT) {
      lrect(ctx, ox, oy, cx - 4, top + 7, 2, 1, NEUTRAL.ink);
      lpx(ctx, ox, oy, cx - 5, top + 10, t.skinShadow);
    } else if (dir === DIR_RIGHT) {
      lrect(ctx, ox, oy, cx + 2, top + 7, 2, 1, NEUTRAL.ink);
      lpx(ctx, ox, oy, cx + 4, top + 10, t.skinShadow);
    }

    // Smug smirk
    if (dir === DIR_DOWN) {
      lrect(ctx, ox, oy, cx - 1, top + 12, 3, 1, t.skinShadow);
      lpx(ctx, ox, oy, cx + 2, top + 11, t.skinShadow);
    }

  } else {
    // Civilian — simple hair cap
    lrect(ctx, ox, oy, cx - hw + 1, top + 1, hw * 2 - 2, 2, t.hair);
    // Simple eyes
    if (dir === DIR_DOWN) {
      lpx(ctx, ox, oy, cx - 3, top + 7, NEUTRAL.ink);
      lpx(ctx, ox, oy, cx + 2, top + 7, NEUTRAL.ink);
    } else if (dir === DIR_LEFT) {
      lpx(ctx, ox, oy, cx - 3, top + 7, NEUTRAL.ink);
    } else if (dir === DIR_RIGHT) {
      lpx(ctx, ox, oy, cx + 2, top + 7, NEUTRAL.ink);
    }
  }
}

// ============================================================
// BODY — separate drawer per type
// ============================================================

function drawVirginBody(
  ctx: CanvasRenderingContext2D, ox: number, oy: number,
  t: Tones, dir: number, pose: Record<string, number>,
) {
  const bt = BODY.virgin;
  const dx = pose.torsoDx ?? 0;
  const dy = pose.torsoDy ?? 0;
  const cx = BASE.headCx;
  const top = BASE.torsoTop + dy;
  const bellyTop = bt.bellyTop + dy;
  const bot = BASE.torsoBottom + dy;

  // Outline — pear silhouette (narrow top, wide belly)
  // Top of shoulders (narrower)
  lrect(ctx, ox, oy, cx - bt.shoulderHalf - 1, top, 1, bellyTop - top + 1, t.outline);
  lrect(ctx, ox, oy, cx + bt.shoulderHalf,     top, 1, bellyTop - top + 1, t.outline);
  lrect(ctx, ox, oy, cx - bt.shoulderHalf,     top, bt.shoulderHalf * 2, 1, t.outline);
  // Belly bulge (wider)
  lrect(ctx, ox, oy, cx - bt.bellyHalf - 1, bellyTop, 1, bot - bellyTop + 1, t.outline);
  lrect(ctx, ox, oy, cx + bt.bellyHalf,     bellyTop, 1, bot - bellyTop + 1, t.outline);
  lrect(ctx, ox, oy, cx - bt.bellyHalf,     bot,      bt.bellyHalf * 2, 1, t.outline);
  // Transition from shoulders to belly
  lrect(ctx, ox, oy, cx - bt.bellyHalf - 1, bellyTop - 1, bt.bellyHalf - bt.shoulderHalf + 1, 1, t.outline);
  lrect(ctx, ox, oy, cx + bt.shoulderHalf,  bellyTop - 1, bt.bellyHalf - bt.shoulderHalf + 1, 1, t.outline);

  // Shirt fill — baggy dark hoodie
  // Top (shoulders area)
  lrect(ctx, ox, oy, cx - bt.shoulderHalf, top + 1, bt.shoulderHalf * 2, bellyTop - top - 1, t.shirtBase);
  // Belly fill (wider)
  lrect(ctx, ox, oy, cx - bt.bellyHalf, bellyTop, bt.bellyHalf * 2, bot - bellyTop, t.shirtBase);
  // Belly shadow — suggests roundness
  lrect(ctx, ox, oy, cx - bt.bellyHalf, bot - 2, bt.bellyHalf * 2, 1, t.shirtDeep);
  // Belly highlight
  if (dir !== DIR_LEFT) {
    lrect(ctx, ox, oy, cx + bt.bellyHalf - 1, bellyTop + 1, 1, 3, t.shirtHi);
  }

  // Accent — virgin scarf (cyan-ish) drapes across shoulders
  lrect(ctx, ox, oy, cx - bt.shoulderHalf, top + 2, bt.shoulderHalf * 2, 1, t.accent);
  if (dir !== DIR_UP) {
    // Scarf end hanging down the side
    lpx(ctx, ox, oy, cx - bt.shoulderHalf + 1, top + 3, t.accent);
    lpx(ctx, ox, oy, cx - bt.shoulderHalf + 1, top + 4, t.accentHi);
  }
}

function drawChadBody(
  ctx: CanvasRenderingContext2D, ox: number, oy: number,
  t: Tones, dir: number, pose: Record<string, number>,
) {
  const bt = BODY.chad;
  const dx = pose.torsoDx ?? 0;
  const dy = pose.torsoDy ?? 0;
  const cx = BASE.headCx;
  const top = BASE.torsoTop + dy;
  const bellyTop = bt.bellyTop + dy;
  const bot = BASE.torsoBottom + dy;

  // Outline — inverted V silhouette (wide shoulders, narrow waist)
  lrect(ctx, ox, oy, cx - bt.shoulderHalf - 1, top, 1, bellyTop - top + 1, t.outline);
  lrect(ctx, ox, oy, cx + bt.shoulderHalf,     top, 1, bellyTop - top + 1, t.outline);
  lrect(ctx, ox, oy, cx - bt.shoulderHalf,     top, bt.shoulderHalf * 2, 1, t.outline);
  // Waist (narrower)
  lrect(ctx, ox, oy, cx - bt.bellyHalf - 1, bellyTop, 1, bot - bellyTop + 1, t.outline);
  lrect(ctx, ox, oy, cx + bt.bellyHalf,     bellyTop, 1, bot - bellyTop + 1, t.outline);
  lrect(ctx, ox, oy, cx - bt.bellyHalf,     bot,      bt.bellyHalf * 2, 1, t.outline);
  // Shoulder-to-waist taper
  lrect(ctx, ox, oy, cx - bt.shoulderHalf - 1, bellyTop - 1, bt.shoulderHalf - bt.bellyHalf + 1, 1, t.outline);
  lrect(ctx, ox, oy, cx + bt.bellyHalf,        bellyTop - 1, bt.shoulderHalf - bt.bellyHalf + 1, 1, t.outline);

  // Skin exposed at shoulders + chest (tank top)
  // Neck / upper chest is skin
  lrect(ctx, ox, oy, cx - 3, top + 1, 6, 2, t.skin);

  // Tank top fill — narrower than shoulders, straps over chest
  const tankL = cx - bt.chestHalf + 1;
  const tankR = cx + bt.chestHalf - 1;
  lrect(ctx, ox, oy, tankL, top + 3, tankR - tankL + 1, bot - top - 3, t.shirtBase);
  // Tank highlight
  if (dir !== DIR_LEFT) {
    lrect(ctx, ox, oy, tankR - 1, top + 4, 1, bot - top - 5, t.shirtHi);
  }
  // Tank shadow
  lrect(ctx, ox, oy, tankL, top + 4, 1, bot - top - 5, t.shirtDeep);

  // Exposed shoulders (skin on the outside of the tank)
  lrect(ctx, ox, oy, cx - bt.shoulderHalf, top + 1, bt.shoulderHalf - bt.chestHalf + 1, 3, t.skin);
  lrect(ctx, ox, oy, cx + bt.chestHalf - 1, top + 1, bt.shoulderHalf - bt.chestHalf + 1, 3, t.skin);
  // Shoulder shadow for muscle
  lpx(ctx, ox, oy, cx - bt.shoulderHalf + 1, top + 2, t.skinShadow);
  lpx(ctx, ox, oy, cx + bt.shoulderHalf - 2, top + 2, t.skinShadow);

  // Chest pec definition — thin V down chest
  if (dir === DIR_DOWN) {
    lpx(ctx, ox, oy, cx - 2, top + 5, t.shirtDeep);
    lpx(ctx, ox, oy, cx + 1, top + 5, t.shirtDeep);
    lpx(ctx, ox, oy, cx - 1, top + 7, t.shirtDeep);
    lpx(ctx, ox, oy, cx,     top + 7, t.shirtDeep);
  }

  // Abs hint on exposed midriff (bottom of tank)
  lpx(ctx, ox, oy, cx - 1, bot - 2, darken(t.shirtBase, 0.25));
  lpx(ctx, ox, oy, cx + 1, bot - 2, darken(t.shirtBase, 0.25));

  // Accent sash — diagonal pink across chest
  lrect(ctx, ox, oy, tankL, top + 6, tankR - tankL + 1, 1, t.accent);
  lpx(ctx, ox, oy, tankL, top + 6, t.accentHi);
}

function drawCivilianBody(
  ctx: CanvasRenderingContext2D, ox: number, oy: number,
  t: Tones, dir: number, pose: Record<string, number>,
) {
  const bt = BODY.civilian;
  const dy = pose.torsoDy ?? 0;
  const cx = BASE.headCx;
  const top = BASE.torsoTop + dy;
  const bot = BASE.torsoBottom + dy;

  // Rectangular silhouette
  lrect(ctx, ox, oy, cx - bt.shoulderHalf - 1, top, 1, bot - top + 1, t.outline);
  lrect(ctx, ox, oy, cx + bt.shoulderHalf,     top, 1, bot - top + 1, t.outline);
  lrect(ctx, ox, oy, cx - bt.shoulderHalf, top, bt.shoulderHalf * 2, 1, t.outline);
  lrect(ctx, ox, oy, cx - bt.shoulderHalf, bot, bt.shoulderHalf * 2, 1, t.outline);
  lrect(ctx, ox, oy, cx - bt.shoulderHalf, top + 1, bt.shoulderHalf * 2, bot - top - 1, t.shirtBase);

  // Highlight
  if (dir !== DIR_LEFT) {
    lrect(ctx, ox, oy, cx + bt.shoulderHalf - 1, top + 2, 1, bot - top - 3, t.shirtHi);
  }
  // Shadow
  lrect(ctx, ox, oy, cx - bt.shoulderHalf, top + 2, 1, bot - top - 3, t.shirtDeep);
}

// ============================================================
// ARMS (shared with body-type parameterization)
// ============================================================

function drawArms(
  ctx: CanvasRenderingContext2D, ox: number, oy: number,
  t: Tones, dir: number, pose: Record<string, number>,
  body: BodyType,
) {
  const bt = BODY[body];
  const cx = BASE.headCx;
  const torsoTop = BASE.torsoTop + (pose.torsoDy ?? 0);

  // Forward arm
  const afxBase = cx + bt.shoulderHalf;
  const afyBase = torsoTop + 2;
  const afx = afxBase + (pose.armFwdDx ?? 0);
  const afy = afyBase + (pose.armFwdDy ?? 0);
  const armLen = bt.armLen;
  const armW = bt.armHalf * 2;
  // Outline
  lrect(ctx, ox, oy, afx, afy - 1, armW, 1, t.outline);
  lrect(ctx, ox, oy, afx, afy + armLen, armW, 1, t.outline);
  lrect(ctx, ox, oy, afx - 1, afy, 1, armLen, t.outline);
  lrect(ctx, ox, oy, afx + armW, afy, 1, armLen, t.outline);
  // For Chad: show biceps (skin) on upper arm, then sleeve or more skin
  if (body === 'chad') {
    // Entire arm is skin — tank top exposes it
    lrect(ctx, ox, oy, afx, afy, armW, armLen, t.skin);
    // Bicep bulge shadow
    lrect(ctx, ox, oy, afx, afy + 2, 1, 3, t.skinShadow);
    lrect(ctx, ox, oy, afx + armW - 1, afy + 2, 1, 3, t.skinHi);
  } else {
    // Virgin / civilian — sleeve + skin hand
    lrect(ctx, ox, oy, afx, afy, armW, armLen - 2, t.shirtBase);
    lrect(ctx, ox, oy, afx, afy, 1, armLen - 2, t.shirtDeep);
    // Hand
    lrect(ctx, ox, oy, afx, afy + armLen - 2, armW, 2, t.skin);
  }

  // Back arm — hidden partially behind torso on UP, fully shown on DOWN/SIDE
  if (dir !== DIR_UP) {
    const abxBase = cx - bt.shoulderHalf - armW;
    const abx = abxBase + (pose.armBackDx ?? 0);
    const aby = afyBase + (pose.armBackDy ?? 0);
    lrect(ctx, ox, oy, abx, aby - 1, armW, 1, t.outline);
    lrect(ctx, ox, oy, abx, aby + armLen, armW, 1, t.outline);
    lrect(ctx, ox, oy, abx - 1, aby, 1, armLen, t.outline);
    lrect(ctx, ox, oy, abx + armW, aby, 1, armLen, t.outline);
    if (body === 'chad') {
      lrect(ctx, ox, oy, abx, aby, armW, armLen, t.skin);
      lrect(ctx, ox, oy, abx, aby + 2, 1, 3, t.skinShadow);
    } else {
      lrect(ctx, ox, oy, abx, aby, armW, armLen - 2, t.shirtBase);
      lrect(ctx, ox, oy, abx + armW - 1, aby, 1, armLen - 2, t.shirtHi);
      lrect(ctx, ox, oy, abx, aby + armLen - 2, armW, 2, t.skin);
    }
  }
}

// ============================================================
// LEGS (shared — pants + shoes)
// ============================================================

function drawLegs(
  ctx: CanvasRenderingContext2D, ox: number, oy: number,
  t: Tones, dir: number, pose: Record<string, number>,
  body: BodyType,
) {
  const bt = BODY[body];
  void dir;
  const cx = BASE.headCx;
  const llxBase = cx - 3;
  const lrxBase = cx + 1;
  const lyBase = BASE.hipY;

  // Left leg
  const llx = llxBase + (pose.legLDx ?? 0);
  const lly = lyBase + (pose.legLDy ?? 0);
  const legH = BASE.footY - BASE.hipY;
  lrect(ctx, ox, oy, llx - 1, lly, 1, legH, t.outline);
  lrect(ctx, ox, oy, llx + 3, lly, 1, legH, t.outline);
  lrect(ctx, ox, oy, llx, lly, 3, legH, t.pants);
  lrect(ctx, ox, oy, llx, lly, 1, legH, t.pantsShadow);
  lrect(ctx, ox, oy, llx + 2, lly, 1, legH, t.pantsHi);
  // Shoe
  lrect(ctx, ox, oy, llx - 1, BASE.footY, 5, BASE.footBottom - BASE.footY + 1, t.outline);
  lrect(ctx, ox, oy, llx, BASE.footY + 1, 3, BASE.footBottom - BASE.footY, t.shoe);

  // Right leg
  const lrx = lrxBase + (pose.legRDx ?? 0);
  const lry = lyBase + (pose.legRDy ?? 0);
  lrect(ctx, ox, oy, lrx - 1, lry, 1, legH, t.outline);
  lrect(ctx, ox, oy, lrx + 3, lry, 1, legH, t.outline);
  lrect(ctx, ox, oy, lrx, lry, 3, legH, t.pants);
  lrect(ctx, ox, oy, lrx, lry, 1, legH, t.pantsShadow);
  lrect(ctx, ox, oy, lrx + 2, lry, 1, legH, t.pantsHi);
  lrect(ctx, ox, oy, lrx - 1, BASE.footY, 5, BASE.footBottom - BASE.footY + 1, t.outline);
  lrect(ctx, ox, oy, lrx, BASE.footY + 1, 3, BASE.footBottom - BASE.footY, t.shoe);
  void bt;
}

// ============================================================
// WEAPONS
// ============================================================

function drawWeapon(
  _ctx: CanvasRenderingContext2D, _ox: number, _oy: number,
  _dir: number, _pose: Record<string, number>, _faction: Faction,
  _isAttacking: boolean,
) {
  // Held props (chad's phone, virgin's book) intentionally disabled —
  // they read as "a thing spawning next to the avatar" rather than as
  // an integrated body silhouette, especially during the brighter
  // attack-frame variant. Restore by reverting this function to the
  // commit before this change.
  return;
}

// ============================================================
// DEATH overlay — extra tilt/fade on death frames
// ============================================================

function applyDeathTint(
  ctx: CanvasRenderingContext2D, ox: number, oy: number, frame: number,
) {
  if (frame === 0) return;
  // Desaturate overlay — dim all pixels in cell with semi-transparent
  // dark layer. Simulates KO.
  const alpha = frame === 1 ? 0.25 : 0.5;
  ctx.fillStyle = `rgba(11,9,18,${alpha})`;
  ctx.fillRect(ox, oy, FRAME_W, FRAME_H);
}

// ============================================================
// CELL COMPOSER
// ============================================================

function drawCell(
  ctx: CanvasRenderingContext2D,
  ox: number, oy: number,
  c: CharacterColors, body: BodyShape,
  dir: number,
  state: AnimState,
  frame: number,
) {
  void body;
  const faction = classifyFaction(c.shirt);
  const btype: BodyType = faction === 'civilian' ? 'civilian' : faction;
  const tones = buildTones(c, faction);
  const pose = getPose(btype, state, frame);

  // Draw order: legs → body → arms → head → weapon
  drawLegs(ctx, ox, oy, tones, dir, pose, btype);
  if (btype === 'virgin') drawVirginBody(ctx, ox, oy, tones, dir, pose);
  else if (btype === 'chad') drawChadBody(ctx, ox, oy, tones, dir, pose);
  else drawCivilianBody(ctx, ox, oy, tones, dir, pose);
  drawArms(ctx, ox, oy, tones, dir, pose, btype);
  drawHeadChibi(ctx, ox, oy, tones, dir, pose.headDy ?? 0, btype);
  drawWeapon(ctx, ox, oy, dir, pose, faction, state === 'attack');

  // Death darken pass
  if (state === 'death') {
    applyDeathTint(ctx, ox, oy, frame);
  }
}

// ============================================================
// SHEET BUILDER
// ============================================================

export function makeCharacterSheet(c: CharacterColors, body: BodyShape = 'square'): Sheet {
  const sheetW = FRAME_W * SHEET_COLS;
  const sheetH = FRAME_H * SHEET_ROWS;
  const { c: canvas, ctx } = newCanvas(sheetW, sheetH);

  const states: Array<[AnimState, number, number]> = [
    ['idle',   STATE_META.idle.rowOffset,   STATE_META.idle.frameCount],
    ['walk',   STATE_META.walk.rowOffset,   STATE_META.walk.frameCount],
    ['attack', STATE_META.attack.rowOffset, STATE_META.attack.frameCount],
    ['hurt',   STATE_META.hurt.rowOffset,   STATE_META.hurt.frameCount],
    ['death',  STATE_META.death.rowOffset,  STATE_META.death.frameCount],
  ];

  for (const [state, rowOffset, frameCount] of states) {
    for (let dir = 0; dir < 4; dir++) {
      const row = rowOffset + dir;
      for (let frame = 0; frame < frameCount; frame++) {
        const ox = frame * FRAME_W;
        const oy = row * FRAME_H;
        drawCell(ctx, ox, oy, c, body, dir, state, frame);
      }
    }
  }

  return {
    texture: makeTexture(canvas),
    cols: SHEET_COLS,
    rows: SHEET_ROWS,
    frameW: FRAME_W,
    frameH: FRAME_H,
    states: {
      idle:   STATE_META.idle,
      walk:   STATE_META.walk,
      attack: STATE_META.attack,
      hurt:   STATE_META.hurt,
      death:  STATE_META.death,
    },
  };
}
